import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface AdminRouteProps {
    children: ReactElement;
}

/**
 * Protected route component that requires system administrator privileges.
 * Checks for the 'admin' custom claim in the user's Firebase token.
 * Redirects to home page if user is not authenticated or not a system admin.
 */
export default function AdminRoute({ children }: AdminRouteProps) {
    const { currentUser, loading } = useAuth();
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        const checkAdminStatus = async () => {
            if (!currentUser) {
                setIsAdmin(false);
                setChecking(false);
                return;
            }

            try {
                const tokenResult = await currentUser.getIdTokenResult();
                // Only allow system admins, not org admins with temporary elevation
                setIsAdmin(tokenResult.claims.admin === 'system');
            } catch (error) {
                console.error('Error checking admin status:', error);
                setIsAdmin(false);
            } finally {
                setChecking(false);
            }
        };

        if (!loading) {
            checkAdminStatus();
        }
    }, [currentUser, loading]);

    // Show loading state while checking authentication and admin status
    if (loading || checking) {
        return <div className="loading">Loading...</div>;
    }

    // Redirect to home if not authenticated or not an admin
    if (!currentUser || !isAdmin) {
        return <Navigate to="/" replace />;
    }

    // User is authenticated and is a system admin
    return children;
}
